import { useEffect, useRef, useState, type RefObject } from 'react'
import type { ProductImage } from '../api/types'
import { useT } from '../i18n'

/**
 * Die Bilder eines Produkts.
 *
 * Ein Streifen mit einem Bild je Seite, gesteuert auf dem Rechner über die
 * Vorschau darunter und am Telefon über Wischen und Punkte. Ein Streifen für
 * beides und nicht zwei Bauweisen: Welches Bild gerade zu sehen ist, weiß dann
 * genau eine Stelle — der Beobachter unten —, und Vorschau und Punkte zeigen
 * dasselbe an.
 *
 * Jedes Bild steht auf der hellen Produktbühne und ganz (contain), nie
 * beschnitten: Ein Händlerfoto im Hochformat verlöre bei cover Kopf und Fuß,
 * und genau das sind bei einer Flasche Etikett und Boden.
 */
export function Gallery({ bilder, produktName }: { bilder: ProductImage[]; produktName: string }) {
  const t = useT()
  const streifen = useRef<HTMLUListElement>(null)
  const aktiv = useSichtbaresBild(streifen, bilder.length)

  if (bilder.length === 0) {
    return (
      <div className="sf-galerie">
        <div
          className="sf-galerie-bild sf-galerie-leer"
          role="img"
          aria-label={t('galerie.keinBildZu', { name: produktName })}
        />
      </div>
    )
  }

  function zeige(index: number) {
    const ziel = streifen.current?.children[index]
    if (!(ziel instanceof HTMLElement) || !streifen.current) return
    // scrollTo auf dem Streifen statt scrollIntoView auf dem Bild: Letzteres
    // rollte auch die ganze Seite, sobald der Streifen nicht ganz im Bild war,
    // und der Klick auf ein Vorschaubild warf den Kunden nach oben.
    streifen.current.scrollTo({
      left: ziel.offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }

  return (
    <div className="sf-galerie">
      <ul className="sf-galerie-streifen" ref={streifen}>
        {bilder.map((bild, index) => (
          <li key={bild.url}>
            <div className="sf-galerie-bild">
              <img
                src={bild.url}
                /* Fehlt der Alternativtext aus dem Backoffice, ist der
                   Produktname besser als ein leeres alt — das hieße „dieses
                   Bild trägt keine Bedeutung", und das stimmt hier nie. */
                alt={bild.altText || produktName}
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : undefined}
                decoding="async"
              />
            </div>
          </li>
        ))}
      </ul>

      {bilder.length > 1 && (
        <>
          <ul className="sf-galerie-vorschau">
            {bilder.map((bild, index) => (
              <li key={bild.url}>
                <button
                  type="button"
                  aria-current={index === aktiv}
                  aria-label={t('galerie.bildAnzeigen', { nummer: index + 1, anzahl: bilder.length })}
                  onClick={() => zeige(index)}
                >
                  <img src={bild.url} alt="" loading="lazy" decoding="async" />
                </button>
              </li>
            ))}
          </ul>

          <ol className="sf-galerie-punkte">
            {bilder.map((bild, index) => (
              <li key={bild.url}>
                <button
                  type="button"
                  aria-current={index === aktiv}
                  aria-label={t('galerie.bildAnzeigen', { nummer: index + 1, anzahl: bilder.length })}
                  onClick={() => zeige(index)}
                />
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  )
}

/**
 * Welches Bild gerade zu sehen ist — gemessen, nicht gezählt.
 *
 * Aus der Rollposition errechnet stimmte es nur, solange alle Bilder gleich
 * breit sind; der Beobachter fragt den Browser, und der weiß es.
 */
function useSichtbaresBild(streifen: RefObject<HTMLUListElement>, anzahl: number): number {
  const [aktiv, setAktiv] = useState(0)

  useEffect(() => {
    const wurzel = streifen.current
    if (!wurzel || anzahl < 2) return

    const kinder = Array.from(wurzel.children)
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        for (const eintrag of eintraege) {
          if (eintrag.isIntersecting) setAktiv(kinder.indexOf(eintrag.target))
        }
      },
      // Mehr als die Hälfte sichtbar heißt: das ist das Bild, das man sieht.
      { root: wurzel, threshold: 0.6 },
    )
    for (const kind of kinder) beobachter.observe(kind)
    return () => beobachter.disconnect()
  }, [streifen, anzahl])

  return aktiv
}
